import { Fragment, useEffect, useRef, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronRight, CloudUpload, CloudDownload, Globe, Sparkles } from "lucide-react";
import "./pr-creation.css";

const PROBLEM =
  "Ariba auto-selects a contract when a supplier holds several, but often picks the wrong one. Requesters can't tell which is correct and leave the default unchanged, causing downstream PO errors, invoice mismatches, and manual rework.";
const PLACEHOLDER = "e.g. Industrial sensors for HVAC maintenance across our Pune facility";
const CHIPS = [
  "Industrial sensors for HVAC maintenance",
  "Software licences for ERP upgrade",
  "Logistics packaging materials Q3",
];
const SUPPLIERS = ["Siemens AG (3 contracts)", "Bosch GmbH (2 contracts)", "ABB Group (4 contracts)"];

const REC = {
  recommended: { badge: "AI RECOMMENDED CONTRACT", id: "SIE-CT-2024-011", desc: "Industrial Automation Equipment", validity: "Valid: Mar 2024 - Feb 2027" },
  default: { badge: "ARIBA DEFAULT (AUTO-SELECTED)", id: "SIE-CT-2023-004", desc: "Facility Maintenance & Spares", validity: "Would cause downstream mismatch" },
  commodity: "39121500 — Sensors & Automation Devices",
  confidence: "97%",
  explanation:
    "The description matches “Industrial Automation Equipment” more closely than the account's default contract. Key terms in the request align with this contract's scope and current validity window, reducing the risk of a PO or invoice mismatch later.",
};

const PREVIEW: [string, string][] = [
  ["Supplier", "Siemens AG"],
  ["Contract", "SIE-CT-2024-011 — Industrial Automation Equipment"],
  ["Commodity Code", "39121500 — Sensors & Automation Devices"],
  ["Description", "Industrial sensors for HVAC maintenance"],
];

const PUSH_STEPS = [
  { icon: "check" as const, title: "Payload prepared", sub: "PR-2026-11842 validated against the Ariba schema" },
  { icon: "sparkles" as const, title: "AI pre-flight", sub: "Field mapping, duplicates and tolerance rules checked — 0 blocking errors" },
  { icon: "check" as const, title: "Pushed", sub: "Accepted by Ariba Buying at 7:30:54 pm" },
];
const PUSH_METRICS = [
  { title: "RECORDS POSTED", value: "1 of 1", sub: "No retries needed" },
  { title: "FIELDS AUTO-MAPPED", value: "28", sub: "4 corrected by AI" },
  { title: "ROUND-TRIP", value: "1.4s", sub: "API acknowledged" },
];
const PULL_NARRATIVE = "37 purchase orders pulled from Ariba and classified by the AI in one pass.";
const PULL_ROWS: [string, number][] = [
  ["Fully received", 23],
  ["Partially received", 8],
  ["Awaiting receipt", 6],
];

function ProblemCard() {
  return (
    <div className="pr-creation__problem">
      <span className="pr-creation__problem-title">The problem</span>
      <p className="pr-creation__problem-text">{PROBLEM}</p>
    </div>
  );
}

interface ActionCardProps {
  description: string;
  setDescription: (v: string) => void;
  supplier: string;
  setSupplier: (v: string) => void;
  onAnalyse: () => void;
}

function ActionCard({ description, setDescription, supplier, setSupplier, onAnalyse }: ActionCardProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const canAnalyse = description.trim().length > 0 && supplier.length > 0;

  return (
    <div className="pr-creation__action">
      <div className="pr-creation__field">
        <label className="pr-creation__label">Describe what you need to purchase:</label>
        <textarea
          className="pr-creation__textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={PLACEHOLDER}
          rows={4}
        />
        <div className="pr-creation__chips">
          {CHIPS.map((c) => (
            <button key={c} type="button" className="pr-creation__chip" onClick={() => setDescription(c)}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="pr-creation__field">
        <label className="pr-creation__label">Supplier</label>
        <div ref={ref} className="pr-creation__select-wrap">
          <button
            type="button"
            className="pr-creation__select-btn"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span className={supplier ? "" : "is-placeholder"}>{supplier || "Select a supplier..."}</span>
            <ChevronDown size={16} />
          </button>
          {open && (
            <div className="pr-creation__select-dropdown" role="listbox">
              {SUPPLIERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="option"
                  aria-selected={supplier === s}
                  className={`pr-creation__select-option${supplier === s ? " is-selected" : ""}`}
                  onClick={() => {
                    setSupplier(s);
                    setOpen(false);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <button type="button" className="pr-creation__analyse-btn" disabled={!canAnalyse} onClick={onAnalyse}>
        <Sparkles size={16} />
        Analyse &amp; Recommend
      </button>
    </div>
  );
}

function ContractCard({ variant, data }: { variant: "rec" | "def"; data: { badge: string; id: string; desc: string; validity: string } }) {
  return (
    <div className={`pr-creation__rec-card pr-creation__rec-card--${variant}`}>
      <span className="pr-creation__rec-badge">{data.badge}</span>
      <div>
        <div className="pr-creation__rec-id">{data.id}</div>
        <div className="pr-creation__rec-desc">{data.desc}</div>
      </div>
      <span className="pr-creation__rec-validity">{data.validity}</span>
    </div>
  );
}

function RecommendationPanel() {
  return (
    <div className="pr-creation__panel pr-creation__fade">
      <h3 className="pr-creation__panel-title">AI Recommendation</h3>
      <div className="pr-creation__rec-cards">
        <ContractCard variant="rec" data={REC.recommended} />
        <ContractCard variant="def" data={REC.default} />
      </div>
      <div className="pr-creation__rec-meta">
        <div className="pr-creation__rec-meta-item">
          <span className="pr-creation__rec-meta-label">Commodity Code</span>
          <span className="pr-creation__rec-meta-value">{REC.commodity}</span>
        </div>
        <div className="pr-creation__rec-meta-item" style={{ flex: "0 0 auto" }}>
          <span className="pr-creation__rec-meta-label">Confidence Score</span>
          <span className="pr-creation__rec-confidence">{REC.confidence}</span>
        </div>
      </div>
      <div className="pr-creation__rec-explain">
        <p>{REC.explanation}</p>
      </div>
    </div>
  );
}

function PushPanel() {
  return (
    <div className="pr-creation__push pr-creation__fade">
      <div className="pr-creation__push-steps">
        {PUSH_STEPS.map((s, i) => (
          <Fragment key={s.title}>
            <div className="pr-creation__push-step">
              <div className="pr-creation__push-step-head">
                {s.icon === "check" ? (
                  <CheckCircle2 size={14} style={{ color: "#0e705b" }} />
                ) : (
                  <Sparkles size={14} style={{ color: "#7c3aed" }} />
                )}
                <span className="pr-creation__push-step-title">{s.title}</span>
              </div>
              <span className="pr-creation__push-step-sub">{s.sub}</span>
            </div>
            {i < PUSH_STEPS.length - 1 && <ChevronRight size={16} className="pr-creation__push-chevron" />}
          </Fragment>
        ))}
      </div>
      <div className="pr-creation__push-metrics">
        {PUSH_METRICS.map((m) => (
          <div key={m.title} className="pr-creation__push-metric">
            <span className="pr-creation__push-metric-title">{m.title}</span>
            <span className="pr-creation__push-metric-value">{m.value}</span>
            <span className="pr-creation__push-metric-sub">{m.sub}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PullPanel() {
  const max = 23;
  return (
    <div className="pr-creation__pull pr-creation__fade">
      <span className="pr-creation__pull-narrative">{PULL_NARRATIVE}</span>
      <div className="pr-creation__pull-rows">
        {PULL_ROWS.map(([label, count]) => (
          <div key={label} className="pr-creation__pull-row">
            <span className="pr-creation__pull-label">{label}</span>
            <div className="pr-creation__pull-track">
              <div className="pr-creation__pull-bar" style={{ width: `${(count / max) * 100}%` }} />
            </div>
            <span className="pr-creation__pull-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface PRSubmissionPanelProps {
  submitted: boolean;
  onSubmit: () => void;
  pushed: boolean;
  onPush: () => void;
  pulled: boolean;
  onPull: () => void;
}

function PRSubmissionPanel({ submitted, onSubmit, pushed, onPush, pulled, onPull }: PRSubmissionPanelProps) {
  return (
    <div className="pr-creation__panel pr-creation__fade">
      <div className="flex justify-between">
        <h3 className="pr-creation__panel-title">PR Submission Preview</h3>
        <button type="button" className={`pr-creation__submit-btn${submitted ? " is-done" : ""}`} disabled={submitted} onClick={onSubmit}>
          {submitted && <CheckCircle2 size={16} />}
          {submitted ? "PR Submitted" : "Submit PR"}
        </button>
      </div>
      
      <div className="pr-creation__preview-grid">
        {PREVIEW.map(([label, value]) => (
          <div key={label} className="pr-creation__preview-item">
            <span className="pr-creation__preview-label">{label}</span>
            <div className="pr-creation__preview-value">{value}</div>
          </div>
        ))}
      </div>

      <div className="pr-creation__sync">
        <div className="pr-creation__sync-head">
          <span className="pr-creation__ai-badge">
            <Sparkles size={12} />
            AI
          </span>
          <span className="pr-creation__sync-title">Sync this requisition</span>
        </div>
        <div className="pr-creation__sync-row">
          <div className="pr-creation__sync-actions">
            <button type="button" className="pr-creation__sync-btn" aria-pressed={pushed} onClick={onPush}>
              <CloudUpload size={14} />
              Push PR to Ariba
            </button>
            <button type="button" className="pr-creation__sync-btn" aria-pressed={pulled} onClick={onPull}>
              <CloudDownload size={14} />
              Pull PO status from Ariba
            </button>
          </div>
          <div className="pr-creation__realm">
            <Globe size={14} />
            <span>Ariba realm: PROD-EU1</span>
          </div>
        </div>
        {pushed && <PushPanel />}
        {pulled && <PullPanel />}
      </div>
    </div>
  );
}

export function PrCreationView() {
  const [description, setDescription] = useState("");
  const [supplier, setSupplier] = useState("");
  const [analysed, setAnalysed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pushed, setPushed] = useState(false);
  const [pulled, setPulled] = useState(false);

  return (
    <div className="pr-creation">
      <div className="pr-creation__content">
        <p className="pr-creation__eyebrow">Purchase Order</p>
        <h2 className="pr-creation__title">PR Creation</h2>

        <ProblemCard />
        <ActionCard
          description={description}
          setDescription={setDescription}
          supplier={supplier}
          setSupplier={setSupplier}
          onAnalyse={() => setAnalysed(true)}
        />
        {analysed && <RecommendationPanel />}
        {analysed && (
          <PRSubmissionPanel
            submitted={submitted}
            onSubmit={() => setSubmitted(true)}
            pushed={pushed}
            onPush={() => setPushed(true)}
            pulled={pulled}
            onPull={() => setPulled(true)}
          />
        )}
      </div>
    </div>
  );
}
